import type { BuildIntentRecord } from '@/store/buildIntent';

/**
 * The build somebody pressed Build for, kept in this browser until it lands.
 *
 * The phone learnt this the expensive way (src/store/buildIntent.ts) and the browser needs
 * it more: the server stores a finished build under its idempotency key and replays it free
 * for a day — but only to someone who still HAS the key. It lived in a module variable here,
 * so a phone browser that discarded the tab mid-build, a reload, or a fetch the browser gave
 * up on left a build the server finished and charged for, and nothing on this side that could
 * ask for it. The next attempt minted a new key and paid again.
 *
 * The phone's record, plus what this client's draft holds that the phone's wizard record does
 * not: the look (`vibe`), whether it is shared (`kind`) and whose it is. The draft itself is
 * deliberately not persisted (see draft.ts), so this is the one copy of the choices that
 * survives a reload — the reserved project row carries the plan too.
 *
 * NEVER the attached document or media: a book is large and a File is gone after a reload.
 * `hadAttachment` and the attachments' NAMES are recorded so a resume can say which files must
 * be chosen again — and refuse to build without them.
 */
export interface WebBuildIntent extends BuildIntentRecord {
  /** The account that pressed Build. Another account on this browser does not inherit it. */
  ownerId: string;
  vibe?: string | null;
  kind?: 'personal' | 'sharable';
  /**
   * When THIS attempt was sent, and a token naming it.
   *
   * `startedAt` cannot answer "might it still be running?": it is the KEY's age, which a retry
   * keeps so the server's replay window is measured right. A retry sent a minute ago under a
   * key minted yesterday is still in flight; its `startedAt` says a day.
   */
  attemptAt?: string;
  attemptId?: string;
  /**
   * The NAMES of what was attached — never the files. A reload loses the files, and a build
   * retried without them published a page whose every `S.media` answered null (F4, 2026-09-30):
   * these are what a resume checks the draft against, and what it names when asking for them
   * again. `text` is the long-text reader's title.
   */
  attachments?: { media: string[]; documents: string[]; text?: string };
  /**
   * A catalogue id this intent's publish registered and could not put a page up under (F21).
   * The next attempt publishes under it rather than registering a second row at `<slug>-2`.
   */
  registeredAppId?: string;
}

const KEY = 'saddle.buildIntent';

/*
 * Which attempt THIS tab sent, in sessionStorage — deliberately not localStorage.
 *
 * The intent lives in localStorage, which every tab of this site shares, so a second tab sees
 * the first tab's build in flight exactly as a reloaded tab sees its own dead one: unreported.
 * Told apart only by age, a reload within ten minutes would never be reported. sessionStorage
 * is per tab and survives that tab's reload (and a phone browser restoring a discarded tab),
 * so "I sent this, and I am a new page" means nobody is listening for it any more.
 */
const OWN_ATTEMPT = 'saddle.buildAttempt';

export function markOwnAttempt(attemptId: string): void {
  try {
    sessionStorage.setItem(OWN_ATTEMPT, attemptId);
  } catch {
    /* Unknown ownership falls back to the age rule in recover(). */
  }
}

export function isOwnAttempt(attemptId: string | undefined): boolean {
  if (!attemptId) return false;
  try {
    return sessionStorage.getItem(OWN_ATTEMPT) === attemptId;
  } catch {
    return false;
  }
}

/*
 * Every access is wrapped, as everywhere else this client touches storage: a private window
 * or blocked site data throws from localStorage itself. Unreadable storage means no record —
 * the build still runs, it just cannot be recovered after a reload, which is where it was.
 */
export function readIntent(): WebBuildIntent | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? 'null') as WebBuildIntent | null;
    return parsed && typeof parsed === 'object' && typeof parsed.goal === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeIntent(record: WebBuildIntent): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* A lost record costs the recovery, never the build. */
  }
}

/**
 * Merge into the record — only while it is still the record for `requestKey`.
 *
 * A build that finishes after the person started a different one must not write its row or
 * its outcome into somebody else's intent.
 */
export function patchIntent(requestKey: string, patch: Partial<WebBuildIntent>): void {
  const current = readIntent();
  if (current && current.requestKey === requestKey) writeIntent({ ...current, ...patch });
}

/** The intent landed: forget it — but only if it is still this one. */
export function clearIntent(requestKey: string): void {
  if (readIntent()?.requestKey !== requestKey) return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* see above */
  }
}
